import { useMutation } from '@apollo/client';
import { Button } from 'components/Button';
import { withNotification } from 'components/Notification';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { Text } from 'components/Text';
import { Toggle } from 'components/Toggle';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import { UPDATE_STORE } from 'gql/mutations';
import { useEffect, useMemo, useState } from 'react';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import { useSettingsSaveState } from 'hooks/useSettingsSaveState';

const Media = withNotification(
  ({ showNotification }: { showNotification: any }) => {
    const { storeId, storeProperties } = useStore();
    const [teamData, setTeamData] = useState({
      requireVideoUpload: false,
      isVideoUploadMandatory: false,
    });

    useEffect(() => {
      if (storeProperties) {
        setTeamData({
          requireVideoUpload:
            storeProperties.resolvePortalOptions.requireVideoUpload ?? false,
          isVideoUploadMandatory:
            storeProperties.resolvePortalOptions.isVideoUploadMandatory ??
            false,
        });
      }
    }, [storeProperties, setTeamData]);
    const mediaResetKey = useMemo(
      () =>
        JSON.stringify({
          requireVideoUpload:
            storeProperties?.resolvePortalOptions?.requireVideoUpload ?? false,
          isVideoUploadMandatory:
            storeProperties?.resolvePortalOptions?.isVideoUploadMandatory ??
            false,
        }),
      [storeProperties?.resolvePortalOptions],
    );
    const { hasUnsavedChanges, markSaved, markSaveFailed } =
      useSettingsSaveState(teamData, mediaResetKey);
    const [
      updateStore,
      { error: updateStoreError, loading: updateStoreLoading },
    ] = useMutation(UPDATE_STORE, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

    const handleUpdateStore = async (): Promise<boolean> => {
      try {
        await updateStore({
          variables: {
            storeId,
            set: {
              resolvePortalOptions: {
                requireVideoUpload: teamData.requireVideoUpload,
                isVideoUploadMandatory: teamData.isVideoUploadMandatory,
              },
            },
          },
        });
        markSaved();
        showNotification(NotificationTypes.success, 'Media settings updated');
        return true;
      } catch (error) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          updateStoreError ? updateStoreError.message : 'Details not updated',
        );
        return false;
      }
    };

    return (
      <SettingsViewWrapper
        sectionId="resolution-portal"
        activeNav="resolution-portal/media"
      >
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleUpdateStore}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-3">
          <div className="text-xl font-bold my-2">Media Settings</div>
          <div className="mt-3">
            <div
              className="md:flex justify-between items-center py-4"
              style={{ width: '50%' }}
            >
              <div className="md:flex items-center">
                <Text
                  value="Video Upload"
                  type="body"
                  className="text-gray-600 font-bold"
                />
              </div>
              <div className="flex justify-end">
                <Toggle
                  isEnabled={teamData.requireVideoUpload}
                  onChange={(v: boolean) =>
                    setTeamData({
                      ...teamData,
                      requireVideoUpload: v,
                      isVideoUploadMandatory: !v
                        ? false
                        : teamData.isVideoUploadMandatory,
                    })
                  }
                />
              </div>
            </div>
            {teamData.requireVideoUpload && (
              <div
                className="md:flex justify-between items-center py-4"
                style={{ width: '50%' }}
              >
                <div className="md:flex items-center">
                  <Text
                    value="Video Upload Mandatory"
                    type="body"
                    className="text-gray-600 font-bold"
                  />
                </div>
                <div className="flex justify-end">
                  <Toggle
                    isEnabled={teamData.isVideoUploadMandatory}
                    onChange={(v: boolean) =>
                      setTeamData({
                        ...teamData,
                        isVideoUploadMandatory: v,
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
          <Button
            onClick={handleUpdateStore}
            size={SizeEnum.md}
            className="bg-blue-600 text-white hover:bg-blue-700 h-max px-6 mt-4"
            isLoading={updateStoreLoading}
          >
            Update Settings
          </Button>
        </div>
      </SettingsViewWrapper>
    );
  },
);

export default Media;
